import React, { useState, useContext, useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import usePostHogDeviceTypeCapture from "../../app/hooks/usePostHogDeviceTypeCapture";
import { LoginOTPWrapper } from "./style";
import * as Sentry from "@sentry/react";
import { sendOtpToMerchant, verifyMerchantOtp } from "../../api/authAPI";
import { NetworkErrorAlertContext } from "../../context/NetworkErrorAlert";
import { getMerchant } from "../../api/usersAPI";
import { SuccessAlertContext } from "../../context/SuccessAlertContext";
import { useMutation, useQueryClient } from "react-query";
import { AxiosError } from "axios";
import Loader from "../../components/common/Loader";
import NetworkErrorAlert from "../../components/common/alerts/NetworkErrorAlert";
import SuccessAlert from "../../components/common/alerts/SuccessAlert";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { useUser } from "../../context/UserContext";
import { updateBusinessId, updateUser } from "./usersSlice";
import { updateBusinessObj } from "../Settings/settingsSlice";
import { useNavigate } from "react-router-dom";
import Otp from "../../components/common/OTP";
import { GetBusinessUserRole } from "../Settings/Team/redux/actions";

type Props = {
  setVerifyOTP: (arg: boolean) => void;
};
type User = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userId: number;
};

const LoginOTP = ({ setVerifyOTP }: Props) => {
  const countryRef = useRef<string>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const agent = usePostHogDeviceTypeCapture();
  const [otp, setOtp] = useState("");
  const [userId, setUserId] = useState("");
  const queryClient = useQueryClient();

  const isDev = process.env.REACT_APP_IS_DEV === "dev";

  const {
    onShowAlert,
    showAlert: errorAlert,
    alertMessage,
  } = useContext(NetworkErrorAlertContext);
  const {
    onShowAlert: onSuccess,
    showAlert: successAlert,
    alertMessage: successMessage,
  } = useContext(SuccessAlertContext);
  const { loginEmail, loginPassword, businessId } = useAppSelector(
    (state) => state.users,
  );
  const { dispatch: localDispatch, updateToken } = useUser();

  useEffect(() => {
    if (businessId && userId) {
      dispatch(
        GetBusinessUserRole({
          businessId: businessId,
          userId: userId,
        }),
      );
    }
  }, [businessId, userId]);

  const postHog = (user: User) => {
    posthog?.identify(user.id.toString(), {
      email: user.email,
      full_name: `${user.firstName} ${user.lastName}`,
      userId: user.id.toString(),
    });
    posthog?.capture("login_b2b", {
      status: "success",
      user_agent: agent,
    });
  };

  const verifyOTP = useMutation(verifyMerchantOtp, {
    onError: (error: AxiosError) => {
      const errorObj = error?.response?.data;
      const errorMessage =
        typeof errorObj?.error === "string"
          ? errorObj.error
          : typeof errorObj === "string"
          ? errorObj
          : "Invalid OTP";

      Sentry.captureException(error, {
        extra: {
          context: "verifyMerchantOtp",
          email: loginEmail,
          errorMessage,
        },
      });

      posthog?.capture("login_b2b", {
        status: "failure",
        failure_reason: errorMessage,
        email: loginEmail,
        user_agent: agent,
      });

      onShowAlert(errorMessage, errorMessage);
    },
    onSuccess: async (data) => {
      setOtp("");
      const user = data?.data;
      const { accountUser } = user;
      countryRef.current = accountUser?.businessCountry;
      setUserId(accountUser?.id);
      const token = user.token;
      updateToken(token);

      try {
        if (token) {
          await merchantDetailMutation.mutateAsync(user.businessOwnerId);
          queryClient.invalidateQueries({
            queryKey: ["merchant", user.businessOwnerId],
          });
        }
      } catch (err) {
        Sentry.captureException({
          extra: {
            context: "getMerchant",
            userId,
            email: loginEmail,
          },
        });
      }

      postHog(accountUser);
      localDispatch({
        type: "updateUser",
        payload: accountUser,
      });

      dispatch(
        updateUser({
          ...user,
          id: user.businessOwnerId,
        }),
      );
    },
  });

  const handleVerifyOTP = (values: string) => {
    const payload = {
      otp: values,
      email: loginEmail,
    };

    if (values.length < 6) {
      onShowAlert("", "The OTP code is not complete");
    } else {
      verifyOTP.mutate(payload);
    }
  };

  const merchantDetailMutation = useMutation(getMerchant, {
    onError: (error: AxiosError) => {
      const errorObj = error?.response?.data;
      const errorMessage =
        typeof errorObj?.error === "string"
          ? errorObj.error
          : "Failed to get merchant details";

      Sentry.captureException(error, {
        extra: {
          context: "getMerchant",
          userId,
          email: loginEmail,
          errorMessage,
        },
      });

      posthog?.capture("merchant_detail_fetch_failure", {
        failure_reason: errorMessage,
        userId,
        email: loginEmail,
        user_agent: agent,
      });

      onShowAlert(errorMessage, errorMessage);
    },

    onSuccess: async (data) => {
      const responseData = data?.data || [];

      localDispatch({
        type: "updateBusiness",
        payload: responseData,
      });
      if (!responseData.length) {
        navigate(`/auth/onboarding`);
      }

      const hasBusiness = responseData.find((bus: any) => bus?.enabled);
      const busId = hasBusiness?.id || responseData[0]?.id;

      if (busId) {
        dispatch(updateBusinessId(busId));
      }

      if (hasBusiness) {
        dispatch(updateBusinessObj(hasBusiness));
      }

      navigate("/dashboard");
      setVerifyOTP(false);
    },
  });

  const handleResend = () => {
    const payload = {
      email: loginEmail,
      password: loginPassword,
    };
    resendOTP.mutate(payload);
  };

  const resendOTP = useMutation(sendOtpToMerchant, {
    onError: (error: AxiosError) => {
      const errorObj = error?.response?.data;
      let errorMessage = "";

      if (typeof errorObj.error === "string") {
        errorMessage = errorObj.error;
      } else if (typeof errorObj.message === "string") {
        errorMessage = errorObj.message;
      } else {
        errorMessage = "Failed to resend OTP";
      }

      onShowAlert(errorMessage, errorMessage);
    },
    onSuccess: async () => {
      onSuccess("Check your email for the OTP");
      setOtp("");
    },
  });

  useEffect(() => {
    if (isDev) {
      setVerifyOTP(false);
      handleVerifyOTP("123456");
    }
  }, []);

  return (
    <LoginOTPWrapper>
      {errorAlert && <NetworkErrorAlert message={alertMessage} />}
      {successAlert && <SuccessAlert message={successMessage} />}

      <Loader isLoading={verifyOTP.isLoading || resendOTP.isLoading} />

        <Otp
          smallText="To log in, enter the verification code sent to your email
          address"
          buttonLabel="Continue"
          value={otp}
          onChange={setOtp}
          onClick={() => handleVerifyOTP(otp)}
          handleClick={handleResend}
          onClose={() => setVerifyOTP(false)}
        />
    </LoginOTPWrapper>
  );
};

export default LoginOTP;
